import { DeleteOutline } from '@mui/icons-material';
import { IconButton, Stack, styled, Typography } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import { useTranslation } from 'react-i18next';
import { useInstantSearch } from 'react-instantsearch';

import { SearchDomain } from '../../types/SearchDomain.ts';

import { useSearchAutocompleteWithHistory } from './SearchAutocompleteHeadless.hook.ts';

export interface SearchBarProps {
  domain?: SearchDomain;
}

const GroupItems = styled('ul')({
  padding: 0,
});

export const SearchAutocompleteHeadless = ({ domain }: SearchBarProps) => {
  const { t } = useTranslation(undefined, { keyPrefix: 'SearchDialog' });

  const { indexUiState } = useInstantSearch(); // Read query from URL state

  const query = indexUiState?.query;
  const initialState = query
    ? {
        id: query,
        label: query,
      }
    : null;

  const { removeRecentSearchItem: removeItem, ...autocompleteProps } = useSearchAutocompleteWithHistory({
    domain,
    limit: 5,
    initialState,
  });

  return (
    <Autocomplete
      {...autocompleteProps}
      fullWidth
      noOptionsText={t('Input.noRecentSearches')}
      groupBy={() => t('Input.recentSearches')}
      renderGroup={(params) => (
        <li key={params.key}>
          <Typography
            variant="subtitle2"
            color="textSecondary"
            sx={{
              paddingX: 3,
              paddingY: 1,
            }}
          >
            {params.group}
          </Typography>
          <GroupItems>{params.children}</GroupItems>
        </li>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder={t('Input.placeholder')}
          sx={(theme) => ({
            background: theme.palette.background.default,
          })}
        />
      )}
      renderOption={(props, option) => {
        const { key, ...optionProps } = props;

        return (
          <Stack direction="row" component="li" key={key} {...optionProps}>
            <Typography
              color="textPrimary"
              sx={{
                flex: 1,
              }}
            >
              {option.label}
            </Typography>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                removeItem(option.label);
              }}
            >
              <DeleteOutline />
            </IconButton>
          </Stack>
        );
      }}
    />
  );
};
